import { Grid, Typography, Tab, PaginationItem, Link } from '@hermes/web-components'
import {
  NavigationsContainer,
  NavigationTab,
  SitemapContainer,
  SitemapItem,
  SitemapPagination,
  SortingTabs
} from './styles'
import useTranslateMessage from '../../hooks/useTranslateMessage'
import useSitemapMeta from '../../hooks/useSitemapMeta'
import PageMeta from '../ProfileMeta'
import { useContext } from 'react'
import { AppDataContext } from '../../providers/AppData'

type SitemapLink = {
  title: string | string[]
  url: string
}

interface SitemapProps {
  types: string[]
  sorting: string[]
  content: SitemapLink[]
  currentType: string
  currentSubType?: string
  currentSorting: string
  currentKeyword?: string
  pagination: {
    page: number
    totalPages: number
  }
}

const Sitemap = ({
  types,
  sorting,
  content,
  currentType,
  currentSubType,
  currentSorting,
  currentKeyword,
  pagination
}: SitemapProps) => {
  const { locale } = useContext(AppDataContext)
  const translate = useTranslateMessage()
  const pageMeta = useSitemapMeta({ sitemapType: currentType, keyword: currentKeyword })
  const subTypePath = currentSubType ? `/${currentSubType}/` : '/'
  const paginationPath = currentSorting ? `${currentType}${subTypePath}${currentSorting}` : currentType
  const pageSubtitle = currentKeyword
    ? translate('htmlSitemap.keywordLocations', {
        keyword: currentKeyword
      })
    : translate(`htmlSitemap.type.${currentType}`)

  return (
    <SitemapContainer container direction="column" justifyContent="space-between">
      <PageMeta {...pageMeta} />
      <Grid
        container
        direction="column"
        gap={{
          xs: '24px',
          md: '30px'
        }}
      >
        <Grid container direction="column" gap="22px">
          <Typography
            color="system.navy900"
            variant="h1"
            fontSize={{ xs: '36px', md: '44px' }}
            lineHeight={{
              xs: '36px',
              md: '56px'
            }}
          >
            {translate('htmlSitemap.title')}
          </Typography>
          <NavigationsContainer container>
            {types.map((type) => (
              <NavigationTab
                component={Link}
                href={`/${locale}/sitemap/${type}`}
                key={type}
                active={currentType === type}
                variant="outlined"
              >
                {translate(`htmlSitemap.type.${type}`)}
              </NavigationTab>
            ))}
          </NavigationsContainer>
        </Grid>
        <Grid
          container
          gap={{
            xs: '16px',
            md: '8px'
          }}
          direction="column"
        >
          <Typography
            fontSize="15px"
            component="h2"
            lineHeight={{
              xs: '20px',
              md: '40px'
            }}
            fontWeight={500}
          >
            {translate('htmlSitemap.subtitle', {
              type: pageSubtitle
            })}
          </Typography>
          <SortingTabs value={currentSorting} variant="scrollable">
            {sorting.map((sort, index) => (
              <Tab
                label={
                  <Link href={`/${locale}/sitemap/${currentType}${subTypePath}${sort}`}>
                    {sort === 'other' ? '#' : sort}
                  </Link>
                }
                key={sort + index}
                value={sort}
              />
            ))}
          </SortingTabs>
        </Grid>
        <Grid container>
          {content.map((item: any) => (
            <SitemapItem container xs={12} sm={6} md={3} key={item.url}>
              <Link href={item.url}>
                {Array.isArray(item.title) ? translate(item.title[0], item.title[1]) : item.title}
              </Link>
            </SitemapItem>
          ))}
        </Grid>
      </Grid>
      {pagination.totalPages > 1 && (
        <Grid container justifyContent="center">
          <SitemapPagination
            page={pagination.page}
            count={pagination.totalPages}
            hideNextButton={pagination.page === pagination.totalPages}
            hidePrevButton={pagination.page === 1}
            renderItem={(item) => {
              const pagination = item.page === 1 ? '' : `/page-${item.page}`

              return item.page ? (
                <Link href={`/${locale}/sitemap/${paginationPath}${pagination}`} key={item.page}>
                  <PaginationItem {...item} />
                </Link>
              ) : (
                <PaginationItem {...item} />
              )
            }}
          />
        </Grid>
      )}
    </SitemapContainer>
  )
}

export default Sitemap
